import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Download, Loader2, Share2, Sparkles, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { DateStep } from "@/pages/ServiceBookingPage";
import { CarPreview } from "./CarPreview";
import { AddonPicker, BuildSummary, WrapPicker } from "./Pickers";
import { money, priceOf, useRenderJob } from "./builderLib";
import { abs } from "./CarPreview";
import { shareCard } from "./tint";

const field = "w-full h-11 rounded-xl px-3 text-sm bg-white/[0.04] border border-white/10 text-white placeholder:text-gray-600 focus:border-sky-500 outline-none transition-colors";
const MAKES = ["Acura", "Audi", "BMW", "Cadillac", "Chevrolet", "Dodge", "Ferrari", "Ford", "GMC", "Honda", "Hyundai", "Jeep", "Kia", "Lamborghini", "Land Rover", "Lexus", "Mazda", "McLaren", "Mercedes-Benz", "Nissan", "Porsche", "Ram", "Rivian", "Subaru", "Tesla", "Toyota", "Volkswagen"];
const YEARS = Array.from({ length: 38 }, (_, i) => String(new Date().getFullYear() + 1 - i));

const VehicleStep = ({ v, setV, busy, go }) => (
  <div className="max-w-xl mx-auto space-y-5 pt-6" data-testid="builder-vehicle-step">
    <h2 className="font-['Outfit'] text-3xl sm:text-4xl text-white">What are we building?</h2>
    <p className="text-gray-400 text-sm">JARVIS will render your exact car, then you can try every wrap, finish and add-on live.</p>
    <div className="grid sm:grid-cols-3 gap-3">
      <select className={field} value={v.year} onChange={(e) => setV({ ...v, year: e.target.value })} data-testid="builder-year">
        <option value="">Year</option>{YEARS.map((y) => <option key={y}>{y}</option>)}
      </select>
      <input className={field} list="builder-makes" placeholder="Make" value={v.make} onChange={(e) => setV({ ...v, make: e.target.value })} data-testid="builder-make" />
      <input className={field} placeholder="Model (e.g. M4)" value={v.model} onChange={(e) => setV({ ...v, model: e.target.value })} data-testid="builder-model" />
      <datalist id="builder-makes">{MAKES.map((m) => <option key={m} value={m} />)}</datalist>
    </div>
    <button onClick={go} disabled={busy || !v.year || !v.make.trim() || !v.model.trim()} className="h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white flex items-center gap-2 transition-colors" data-testid="builder-generate">
      {busy ? <><Loader2 className="w-4 h-4 animate-spin" />JARVIS is rendering your car… (about 20s)</> : <><Sparkles className="w-4 h-4" />Render my car</>}
    </button>
  </div>
);

const BookBuild = ({ v, color, addons, render, onBack }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState({ name: "", phone: "", email: "", address: "" });
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  const send = () => {
    setBusy(true);
    apiPost("/builder/quote", { ...info, ...v, color_id: color.id, addons: addons.map(({ id, color: c }) => ({ id, color: c || "" })), render_url: render || "" })
      .then(setQ).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't save your build")).finally(() => setBusy(false));
  };
  return (
    <div className="max-w-2xl mx-auto space-y-5 pt-4" data-testid="builder-book-step">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-sky-300 text-sm" data-testid="builder-book-back"><ArrowLeft className="w-4 h-4" />Back to my build</button>
      {!q ? <>
        <h2 className="font-['Outfit'] text-3xl text-white">Book your build</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={field} placeholder="Full name *" value={info.name} onChange={set("name")} data-testid="builder-name" />
          <input className={field} placeholder="Phone *" inputMode="tel" value={info.phone} onChange={set("phone")} data-testid="builder-phone" />
          <input className={field} placeholder="Email" value={info.email} onChange={set("email")} data-testid="builder-email" />
          <input className={field} placeholder="Address" value={info.address} onChange={set("address")} data-testid="builder-address" />
        </div>
        <button onClick={send} disabled={busy || !info.name.trim() || info.phone.replace(/\D/g, "").length < 10} className="h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white flex items-center gap-2" data-testid="builder-continue">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}Continue to pick a date
        </button>
      </> : <>
        <p className="text-white">Total <span className="font-['Outfit'] text-2xl" data-testid="builder-quote-total">{money(q.total)}</span> · deposit {money(q.deposit)}</p>
        <DateStep q={q} navigate={navigate} />
      </>}
    </div>
  );
};

const ShareBuild = ({ v, color, price, source }) => {
  const [busy, setBusy] = useState(false);
  const make = async () => {
    const blob = await shareCard(source, { title: `${v.year} ${v.make} ${v.model}`, lines: price.items.slice(1).map((i) => i.name.replace("Wrap material — ", "")), total: money(price.total) });
    return new File([blob], `my-dream-${v.make}-${v.model}.jpg`.replace(/\s+/g, "-").toLowerCase(), { type: "image/jpeg" });
  };
  const download = (f) => { const a = document.createElement("a"); a.href = URL.createObjectURL(f); a.download = f.name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000); };
  const run = (share) => {
    setBusy(true);
    make().then(async (f) => {
      if (share && navigator.canShare?.({ files: [f] })) await navigator.share({ files: [f], title: "My dream build", text: `My ${v.year} ${v.make} ${v.model} in ${color.name} — ${money(price.total)} at Reflections Auto Detailing` }).catch(() => {});
      else download(f);
    }).catch(() => toast.error("Couldn't make the picture", { position: "bottom-center" })).finally(() => setBusy(false));
  };
  return (
    <div className="flex gap-2">
      <button onClick={() => run(true)} disabled={busy || !source} className="h-11 px-4 rounded-xl border border-white/15 text-white hover:bg-white/5 disabled:opacity-40 flex items-center gap-2 text-sm" data-testid="builder-share"><Share2 className="w-4 h-4" />Share</button>
      <button onClick={() => run(false)} disabled={busy || !source} className="h-11 px-4 rounded-xl border border-white/15 text-white hover:bg-white/5 disabled:opacity-40 flex items-center gap-2 text-sm" data-testid="builder-download">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Save picture</button>
    </div>
  );
};

const Studio = ({ cfg, v, base, parts, onBook }) => {
  const [tinted, setTinted] = useState(null);
  const [finish, setFinish] = useState("gloss");
  const [color, setColor] = useState(cfg.colors.find((c) => c.id === "gloss_gloss_white") || cfg.colors[0]);
  const [addons, setAddons] = useState([]);
  const [tab, setTab] = useState("wrap");
  const [render, setRender] = useState(null);
  const [showRender, setShowRender] = useState(false);
  const [job, setJob] = useRenderJob((url) => { setRender({ url, key: sig }); setShowRender(true); toast.success("Your build is rendered", { position: "bottom-center" }); }, (e) => toast.error(e || "Render failed", { position: "bottom-center" }));
  const sig = JSON.stringify([color?.id, addons]);
  useEffect(() => { setShowRender(false); }, [sig]);
  const price = priceOf(cfg, color, addons);
  const go = () => apiPost("/builder/render-build", { ...v, base_url: base, color_id: color.id, addons }).then((r) => setJob(r.job_id)).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't render"));
  const fresh = render && render.key === sig;
  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-10" data-testid="builder-studio">
      <div className="lg:sticky lg:top-6 self-start space-y-3">
        <div className="relative aspect-[1376/768] rounded-2xl overflow-hidden bg-black border border-white/10">
          <CarPreview base={base} parts={parts} render={fresh ? render.url : null} color={color} addons={addons} showRender={showRender && fresh} onCanvas={setTinted} />
          {job && <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-3 text-sky-100 text-sm"><Loader2 className="w-6 h-6 animate-spin text-sky-400" />Rendering your full build…</div>}
          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-black/60 text-sky-200 px-2 py-1 rounded" data-testid="builder-preview-mode">{showRender && fresh ? "AI render" : "Instant preview"}</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={go} disabled={!!job} className="h-11 px-5 rounded-xl border border-sky-400/50 text-white hover:bg-sky-500/10 disabled:opacity-40 flex items-center gap-2 text-sm" data-testid="builder-render-build"><Wand2 className="w-4 h-4 text-sky-300" />Render my build</button>
          {fresh && <button onClick={() => setShowRender(!showRender)} className="text-xs text-gray-400 hover:text-white underline" data-testid="builder-toggle-render">{showRender ? "Show instant preview" : "Show AI render"}</button>}
          <ShareBuild v={v} color={color} price={price} source={showRender && fresh ? abs(render.url) : tinted} />
          <p className="text-[11px] text-gray-500">{v.year} {v.make} {v.model} · {color?.name}</p>
        </div>
      </div>
      <div className="space-y-5">
        <div className="flex gap-2">{[["wrap", "Wrap & finish"], ["addons", "Add-ons"], ["summary", "Total"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex-1 h-10 rounded-xl text-sm border ${tab === k ? "border-sky-400 bg-sky-500/15 text-white" : "border-white/10 text-gray-400"}`} data-testid={`builder-tab-${k}`}>{l}</button>
        ))}</div>
        {tab === "wrap" && <WrapPicker cfg={cfg} finish={finish} setFinish={(f) => { setFinish(f); setColor(cfg.colors.find((c) => c.finish === f)); }} color={color} setColor={setColor} />}
        {tab === "addons" && <AddonPicker cfg={cfg} addons={addons} setAddons={setAddons} />}
        {tab === "summary" && <BuildSummary price={price} />}
        <div className="sticky bottom-3 z-10 flex items-center justify-between gap-3 rounded-2xl bg-[#0b0d10]/95 backdrop-blur border border-white/10 p-4">
          <div><p className="text-[11px] text-gray-500 uppercase tracking-widest">Build total</p><p className="font-['Outfit'] text-3xl text-white" data-testid="builder-live-total">{money(price.total)}</p></div>
          <button onClick={() => onBook({ color, addons, render: fresh ? render.url : "" })} className="h-12 px-6 rounded-xl bg-sky-500 hover:bg-sky-400 text-white" data-testid="builder-book">Book this build</button>
        </div>
      </div>
    </div>
  );
};

export const CarBuilder = ({ open, onClose }) => {
  const [cfg, setCfg] = useState(null);
  const [v, setV] = useState({ year: "", make: "", model: "" });
  const [base, setBase] = useState(null);
  const [parts, setParts] = useState(null);
  const [book, setBook] = useState(null);
  const [job, setJob] = useRenderJob((url, j) => { setParts(j.parts); setBase(url); }, (e) => toast.error(e || "Couldn't render your car"));
  useEffect(() => { if (open && !cfg) apiGet("/builder/config").then(setCfg).catch(() => toast.error("Builder unavailable")); }, [open, cfg]);
  const go = () => apiPost("/builder/render-base", v).then((r) => (r.status === "done" ? (setParts(r.parts), setBase(r.url)) : setJob(r.job_id))).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't start"));
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-[#050607]/[0.97] backdrop-blur-xl overflow-y-auto" data-testid="car-builder">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sky-500 text-xs tracking-[0.3em] uppercase">Let's build my car</p>
              <div className="flex items-center gap-3">
                {base && !book && <button onClick={() => setBase(null)} className="text-xs text-gray-400 hover:text-white" data-testid="builder-change-car">Change car</button>}
                <button onClick={onClose} className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-gray-300 hover:text-white" data-testid="builder-close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            {!cfg ? <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
              : book ? <BookBuild v={v} {...book} onBack={() => setBook(null)} />
                : base ? <Studio cfg={cfg} v={v} base={base} parts={parts} onBook={setBook} />
                  : <VehicleStep v={v} setV={setV} busy={!!job} go={go} />}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
